
import React, { useState, useCallback, useRef, useMemo } from 'react';
import { motion } from 'framer-motion';
import { calculateVitalScore, storeVitalScoreHistory } from '@/utils/vitalScoreUtils';
import { NutritionJournal } from '@/types';
import VitalScoreWeeklySummary from './VitalScoreWeeklySummary';
import { useMenu } from '@/contexts/MenuContext';
import { format } from 'date-fns';
import useUserProfileSync from '@/hooks/useUserProfileSync';
import VitalScoreCard from './vital-score/VitalScoreCard';
import { Card, CardContent } from '@/components/ui/card';

interface VitalScoreProps {
  journal: NutritionJournal;
  user: any;
  containerVariants: any;
  itemVariants: any;
}

interface NutritionTotals {
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
  iron: number;
  calcium: number;
  zinc: number;
  magnesium: number;
  omega3: number;
}

const VitalScoreTab: React.FC<VitalScoreProps> = ({
  journal,
  user,
  containerVariants,
  itemVariants
}) => {
  const [vitalScore, setVitalScore] = useState<any>(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const { getMenuTotals, weeklyMenu } = useMenu();
  const todayName = format(new Date(), 'EEEE').toLowerCase();
  
  const calculationInProgress = useRef(false);
  
  const { getSyncedProfile } = useUserProfileSync({
    userId: user?.id,
    enabled: !!user?.id
  });
  
  const profile = getSyncedProfile();
  const currentWeight = profile?.weight || 70;
  const currentHeight = profile?.height || 170;

  // Memoized custom ingredient totals
  const customIngredientTotals = useMemo((): NutritionTotals => {
    if (!weeklyMenu || !weeklyMenu.days || !weeklyMenu.days[todayName]) {
      return {
        calories: 0, protein: 0, carbs: 0, fat: 0,
        iron: 0, calcium: 0, zinc: 0, magnesium: 0, omega3: 0
      };
    }
    
    const dayData = weeklyMenu.days[todayName];
    const customIngredients = Array.isArray(dayData?.customIngredients)
      ? dayData.customIngredients
      : [];
      
    return customIngredients.reduce(
      (totals: NutritionTotals, ing) => {
        totals.calories += Number(ing.calories) || 0;
        totals.protein += Number(ing.protein) || 0;
        totals.carbs += Number(ing.carbs) || 0;
        totals.fat += Number(ing.fat) || 0;
        totals.iron += Number(ing.iron) || 0;
        totals.calcium += Number(ing.calcium) || 0;
        totals.zinc += Number(ing.zinc) || 0;
        totals.magnesium += Number(ing.magnesium) || 0;
        totals.omega3 += Number(ing.omega3) || 0;
        return totals;
      },
      {
        calories: 0, protein: 0, carbs: 0, fat: 0,
        iron: 0, calcium: 0, zinc: 0, magnesium: 0, omega3: 0
      }
    );
  }, [weeklyMenu, todayName]);

  // Create a stable hash for triggering recalculation
  const dataHash = useMemo(() => {
    if (!journal || !user) return '';
    
    const menuTotals = getMenuTotals ? getMenuTotals(todayName) : null;
    
    return JSON.stringify({
      journalId: journal.id,
      waterIntake: journal.waterIntake,
      energyLevel: journal.energyLevel,
      restLevel: journal.restLevel,
      steps: journal.steps,
      sunExposure: journal.sunExposure,
      customTotals: customIngredientTotals,
      menuTotals: menuTotals,
      weight: currentWeight,
      height: currentHeight
    });
  }, [
    journal?.id, 
    journal?.waterIntake, 
    journal?.energyLevel, 
    journal?.restLevel, 
    journal?.steps, 
    journal?.sunExposure,
    customIngredientTotals,
    getMenuTotals,
    todayName,
    currentWeight,
    currentHeight,
    user
  ]);

  // Calculate score function
  const calculateScore = useCallback(() => {
    if (!journal || !user || calculationInProgress.current) {
      return;
    }

    calculationInProgress.current = true;
    setIsCalculating(true);
    
    try {
      console.log('🔢 VitalScore: Iniciando cálculo');
      console.log('📊 VitalScore: Journal nutrition summary actual:', journal.nutritionSummary);
      
      // Get menu totals
      const defaultTotals: NutritionTotals = {
        calories: 0, protein: 0, carbs: 0, fat: 0,
        iron: 0, calcium: 0, zinc: 0, magnesium: 0, omega3: 0
      };
      
      const menuTotals = getMenuTotals ? getMenuTotals(todayName) || defaultTotals : defaultTotals;
      
      console.log('🍽️ VitalScore: Totales del menú:', menuTotals);
      console.log('🥄 VitalScore: Ingredientes personalizados:', customIngredientTotals);
      
      // Combine menu totals with custom ingredients
      const fullTotals: NutritionTotals = {
        calories: (menuTotals.calories || 0) + (customIngredientTotals.calories || 0),
        protein: (menuTotals.protein || 0) + (customIngredientTotals.protein || 0),
        carbs: (menuTotals.carbs || 0) + (customIngredientTotals.carbs || 0),
        fat: (menuTotals.fat || 0) + (customIngredientTotals.fat || 0),
        iron: (menuTotals.iron || 0) + (customIngredientTotals.iron || 0),
        calcium: (menuTotals.calcium || 0) + (customIngredientTotals.calcium || 0),
        zinc: (menuTotals.zinc || 0) + (customIngredientTotals.zinc || 0),
        magnesium: (menuTotals.magnesium || 0) + (customIngredientTotals.magnesium || 0),
        omega3: (menuTotals.omega3 || 0) + (customIngredientTotals.omega3 || 0),
      };
      
      console.log('🥗 VitalScore: Totales finales combinados:', fullTotals);
      
      // CRITICAL: Ensure goals are properly set before calculating
      const updatedJournal = {
        ...journal,
        nutritionSummary: {
          ...journal.nutritionSummary,
          // Use consumed values from menu
          caloriesConsumed: fullTotals.calories,
          proteinConsumed: fullTotals.protein,
          carbsConsumed: fullTotals.carbs,
          fatConsumed: fullTotals.fat,
          ironConsumed: fullTotals.iron,
          calciumConsumed: fullTotals.calcium,
          zincConsumed: fullTotals.zinc,
          magnesiumConsumed: fullTotals.magnesium,
          omega3Consumed: fullTotals.omega3,
          // Ensure goals are valid numbers
          goalCalories: journal.nutritionSummary?.goalCalories || user?.profile?.targetCalories || 2000,
          goalProtein: journal.nutritionSummary?.goalProtein || user?.profile?.targetProtein || Math.round((currentWeight || 70) * 0.8),
          goalCarbs: journal.nutritionSummary?.goalCarbs || user?.profile?.targetCarbs || 250,
          goalFat: journal.nutritionSummary?.goalFat || user?.profile?.targetFat || 65,
          goalIron: journal.nutritionSummary?.goalIron || user?.profile?.targetIron || (user?.profile?.gender === 'female' ? 18 : 8),
          goalCalcium: journal.nutritionSummary?.goalCalcium || user?.profile?.targetCalcium || 1000,
          goalZinc: journal.nutritionSummary?.goalZinc || user?.profile?.targetZinc || 11,
          goalMagnesium: journal.nutritionSummary?.goalMagnesium || user?.profile?.targetMagnesium || 400,
          goalOmega3: journal.nutritionSummary?.goalOmega3 || user?.profile?.targetOmega3 || 1.6
        }
      };
      
      console.log('📊 VitalScore: Journal actualizado con nutrición y objetivos:', updatedJournal.nutritionSummary);
      
      // User with current values
      const userWithCurrentValues = {
        ...user,
        profile: {
          ...user?.profile,
          weight: currentWeight,
          height: currentHeight
        }
      };
      
      // Calculate vital score
      const score = calculateVitalScore(updatedJournal, userWithCurrentValues, 75);
      
      console.log('⭐ VitalScore: Puntuación calculada:', score);
      
      // Create immutable score object
      const newVitalScore = Object.freeze({
        total: Math.round(score.total) || 50,
        energyRest: Object.freeze({
          score: Math.round(score.energyRest?.score) || 50,
          energyScore: Math.round(score.energyRest?.energyScore) || 50,
          restScore: Math.round(score.energyRest?.restScore) || 50
        }),
        nutrition: Object.freeze({
          score: Math.round(score.nutrition?.score) || 50
        }),
        movement: Object.freeze({
          score: Math.round(score.movement?.score) || 50
        }),
        hydrationSun: Object.freeze({
          waterScore: Math.round(score.hydrationSun?.waterScore) || 50,
          sunScore: Math.round(score.hydrationSun?.sunScore) || 50
        })
      });
      
      console.log('🎯 VitalScore final:', newVitalScore);
      
      setVitalScore(newVitalScore);
      
      // Store in localStorage
      try {
        localStorage.setItem('currentVitalScore', JSON.stringify(newVitalScore));
        const today = new Date().toISOString().split('T')[0];
        storeVitalScoreHistory(today, newVitalScore.total);
      } catch (storageError) {
        console.warn('VitalScore: Error saving to localStorage:', storageError);
      }
      
    } catch (error) {
      console.error("VitalScore: Error calculating:", error);
      
      const defaultScore = Object.freeze({
        total: 50,
        energyRest: Object.freeze({ score: 50, energyScore: 50, restScore: 50 }),
        nutrition: Object.freeze({ score: 50 }),
        movement: Object.freeze({ score: 50 }),
        hydrationSun: Object.freeze({ waterScore: 50, sunScore: 50 })
      });
      
      setVitalScore(defaultScore);
    } finally {
      setIsCalculating(false);
      calculationInProgress.current = false;
    }
  }, [
    journal, 
    user, 
    currentWeight, 
    currentHeight, 
    todayName, 
    getMenuTotals, 
    customIngredientTotals
  ]);

  // Effect to trigger calculation when data changes
  React.useEffect(() => {
    if (journal && user && !calculationInProgress.current) {
      // Small delay to prevent rapid recalculations
      const timeoutId = setTimeout(() => {
        calculateScore();
      }, 100);
      
      return () => clearTimeout(timeoutId);
    }
  }, [dataHash, calculateScore]);

  // Loading state
  if (!vitalScore && isCalculating) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="space-y-6"
      >
        <Card className="mb-6 shadow-sm">
          <CardContent className="p-6">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500 mx-auto mb-4"></div>
              <p className="text-gray-500">Calculando Vital Score...</p>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    );
  }

  // No score state
  if (!vitalScore && !isCalculating) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="space-y-6"
      >
        <Card className="mb-6 shadow-sm">
          <CardContent className="p-6">
            <div className="text-center">
              <p className="text-gray-500">Vital Score no disponible</p>
              <button 
                onClick={calculateScore}
                className="mt-2 px-4 py-2 bg-purple-500 text-white rounded-md hover:bg-purple-600"
                disabled={calculationInProgress.current}
              >
                Calcular Vital Score
              </button>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="space-y-6"
    >
      <div>
        <VitalScoreCard 
          vitalScore={vitalScore}
          currentWeight={currentWeight}
          currentHeight={currentHeight}
        />
      </div>
      
      <VitalScoreWeeklySummary />
    </motion.div>
  );
};

export default VitalScoreTab;
